import { useCallback, useMemo } from "react";
import {
  ALPHA_PERFORMANCE_SERIES_STROKES,
  clipFactorsFromLaunch,
  resolveLaunchClipMs,
} from "../../components/performanceChartV3Utils";
import {
  actualItemsToFactors,
  useActualPerformance,
} from "../../hooks/useActualPerformance";
import { usePerformanceChartData } from "../../hooks/usePerformanceChartData";
import type { FactorEntry } from "../../services/productFactorsService";
import {
  hasDuplicateAlphaPerformanceDates,
} from "./alphaPerformanceMath";
import {
  useAlphaStrategyVsActualComparison,
  type AlphaStrategyVsActualComparisonResult,
} from "./useAlphaStrategyVsActualComparison";

interface UseAlphaStrategyActualPerformanceParams {
  enabled: boolean;
  includeActual: boolean;
  authenticatedUserId?: string;
  launchDate?: string | null;
  selectedKey: string;
}

export interface AlphaStrategyActualPerformanceResult {
  chart: AlphaStrategyVsActualComparisonResult;
  strategyFactors: FactorEntry[];
  alignedStrategyFactors: FactorEntry[];
  actualFactors: FactorEntry[];
  actualError: string | null;
  isActualFetching: boolean;
  retry: () => void;
  retryActual: () => void;
}

/**
 * Shared Alpha performance source for the dashboard and detail page. The V3
 * strategy cache, launch clipping, Actual query, shared inception, and return
 * calculations stay in one path so both screens always report the same values.
 */
export function useAlphaStrategyActualPerformance({
  enabled,
  includeActual,
  authenticatedUserId,
  launchDate,
  selectedKey,
}: UseAlphaStrategyActualPerformanceParams): AlphaStrategyActualPerformanceResult {
  const {
    factors: strategyFactors,
    loading: strategyLoading,
    error: strategyError,
    retry: retryStrategy,
  } = usePerformanceChartData({
    isActive: enabled,
    entityId: 0,
    entityType: "STRATEGY",
    entityName: "Strategy",
    dataSource: "v3-factors",
    forceLightMode: true,
    showTodayFilter: true,
    launchDate,
    seriesColor: ALPHA_PERFORMANCE_SERIES_STROKES.strategy,
  });
  const {
    data: actualData,
    isLoading: actualLoading,
    isFetching: actualFetching,
    error: actualQueryError,
    refetch: refetchActual,
  } = useActualPerformance(
    undefined,
    enabled && includeActual,
    authenticatedUserId
  );
  const launchClipMs = useMemo(() => resolveLaunchClipMs(launchDate), [launchDate]);
  const clippedActualFactors = useMemo(
    () => includeActual
      ? clipFactorsFromLaunch(
        actualData?.items ? actualItemsToFactors(actualData.items) : [],
        launchClipMs
      )
      : [],
    [actualData, includeActual, launchClipMs]
  );
  const actualHasDuplicateDates = useMemo(
    () => hasDuplicateAlphaPerformanceDates(clippedActualFactors),
    [clippedActualFactors]
  );
  const actualFactors = useMemo(
    () => actualHasDuplicateDates ? [] : clippedActualFactors,
    [actualHasDuplicateDates, clippedActualFactors]
  );

  // Actual can begin after Strategy. Do not publish a temporary Strategy-only
  // inception result while the first Actual request is still deciding the
  // shared baseline. Refetches retain cached data, so later retries do not
  // blank a chart the customer is already reading.
  const comparisonStrategyFactors = includeActual && actualLoading
    ? []
    : strategyFactors;

  const comparison = useAlphaStrategyVsActualComparison({
    isActive: enabled,
    selectedKey,
    strategyFactors: comparisonStrategyFactors,
    actualFactors,
    clockKey: authenticatedUserId ?? "",
  });

  const retryActual = useCallback(() => {
    if (!includeActual) return;
    void refetchActual();
  }, [includeActual, refetchActual]);

  const retry = useCallback(() => {
    retryStrategy();
    retryActual();
  }, [retryActual, retryStrategy]);

  const chart = useMemo<AlphaStrategyVsActualComparisonResult>(() => ({
    ...comparison,
    loading: strategyLoading || (includeActual && actualLoading),
    error: strategyError,
  }), [actualLoading, comparison, includeActual, strategyError, strategyLoading]);

  return {
    chart,
    strategyFactors,
    alignedStrategyFactors: comparison.strategyFactorsFromSharedStart,
    actualFactors,
    actualError: includeActual && actualQueryError
      ? "Actual performance could not be loaded."
      : includeActual && actualHasDuplicateDates
        ? "Actual performance could not be calculated safely."
        : null,
    isActualFetching: includeActual && actualFetching,
    retry,
    retryActual,
  };
}

export default useAlphaStrategyActualPerformance;
